/**
 * Page Utility Functions Support Design Rules for Dynamic Removal of Top Padding
 *
 * General Rule:  If a card container, content block, text block, or 1P=1D is following a module (including the NavBar) that has the same background color, the top padding of that component should be removed
 *
 * Exceptions to the rule:
 * 1. If a component is following a card container, it's top padding should never be removed
 * 2. 1P=1D component is the only component that should not follow exception #1 and should have it's padding removed when following a card container with the same background color
 * 3. If a component is following a content block with bleed image styling applied and it's not on small or largest BP screen, it's top padding should not be removed
 * 4. Components inside a responsive container will not impact the removal of padding to components outside the responsive container
 */

/**
 * Determine if component should have top padding as per rules above & add new padding property indicating same
 * @param {Object} currentComponent Component object
 * @param {Number} index Index of the component in original array
 * @param {Object} lastComponent Component object for the component in front of currentComponent
 * @param {Object} breakpoints Object w/ medium & largest properties and boolean values
 * @param {String} currentComponent.content_type Name/Type of the component
 * @param {String} currentComponent.backgroundColor Background color of the the component
 * @returns {Object} Component object w/ new hasTopPadding property added
 */
export const findPaddingValue = (
  currentComponent,
  index,
  lastComponent,
  breakpoints
) => {
  const componentCopy = { ...currentComponent };
  const type = componentCopy.content_type;
  const backgroundColor =
    componentCopy.backgroundColor ||
    (type === 'content_block' && 'grey20') ||
    'white';
  componentCopy.hasTopPadding = true;

  // if this is the first component on the page
  // check if it's background color matches that of the nav bar
  if (!lastComponent && !index) {
    if (backgroundColor === 'white') {
      componentCopy.hasTopPadding = false;
    }
  } else {
    const previousType = lastComponent.content_type;
    // EXCEPTIONS
    switch (previousType) {
      // if current component is not 1P=1D and is following a card container - do not remove padding
      case 'cardContainer':
        if (type !== 'onePurchasedOneDonated') return componentCopy;
        break;
      // if current component is following a responsive container - do not remove padding
      case 'responsiveContainer':
        return componentCopy;
      // if current component is following a header block w/ background images instead of color - do not remove padding
      case 'headerBlock':
        if (
          (lastComponent.mobileBackgroundImage?.src &&
            lastComponent.desktopBackgroundImage?.src) ||
          (lastComponent.ctas?.length === 1 &&
            lastComponent.ctas[0].url.includes('#show-video-modal'))
        )
          return componentCopy;
        break;
      // if current component is following a content block w/ bleed image styling applied and it's not on small BP - do not remove padding
      case 'content_block':
        if (
          lastComponent.imageStyle === 'bleed' &&
          !breakpoints.largest &&
          breakpoints.medium
        ) {
          return componentCopy;
        }
        break;
      default:
        break;
    }
    // In all other cases - compare background colors of components and remove padding if they match
    const previousBackgroundColor =
      lastComponent.backgroundColor ||
      (previousType === 'content_block' && 'grey20') ||
      'white';
    if (previousBackgroundColor === backgroundColor) {
      componentCopy.hasTopPadding = false;
    }
  }
  return componentCopy;
};

/**
 * Map components including any nested in Responsive Container array to add new padding prop to all components
 * Calls Upon above function findPaddingValue
 * @param {Array<Object>} modules Array of module/component objects
 * @param {Object} breakpoints Object w/ medium & largest properties and boolean values
 * @param {string} modules.content_type Type/Name of the module/component
 * @returns {Array<Object>} Array of module/component Objects w/ new hasTopPadding prop added
 */
export const createPaddingProp = (modules, breakpoints) => {
  const newComponentData = modules.map((component, index) => {
    const componentCopy = { ...component };
    const contentType = componentCopy.content_type;
    if (contentType === 'responsiveContainer') {
      // add padding prop to all mobile only components
      if (componentCopy.mobileOnly) {
        const mobileComponentData = componentCopy.mobileOnly.map((c, i) => {
          const lastComponent =
            componentCopy.mobileOnly[i - 1] || modules[index - 1];
          return findPaddingValue(c, i, lastComponent, breakpoints);
        });
        componentCopy.mobileOnly = mobileComponentData;
      }
      // add padding prop to all desktop only components
      if (componentCopy.desktopOnly) {
        const desktopComponentData = componentCopy.desktopOnly.map((c, i) => {
          const lastComponent =
            componentCopy.desktopOnly[i - 1] || modules[index - 1];
          return findPaddingValue(c, i, lastComponent, breakpoints);
        });
        componentCopy.desktopOnly = desktopComponentData;
      }
      return componentCopy;
    }
    const lastComponent = modules[index - 1];
    return findPaddingValue(component, index, lastComponent, breakpoints);
  });
  return newComponentData;
};

export default {};
